import CheckIcon from '@mui/icons-material/Check';
import DownloadIcon from '@mui/icons-material/FileDownload';
import UploadIcon from '@mui/icons-material/FileUpload';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import Paper from '../../apiForm/panel/Paper';
import { downloadFile, readFile } from './downloadUtils';
import ProductsIcon from '../../icons/shelves.svg?react';
import UploadDialog from './FileUpload/UploadDialog';
import useExportApi from './useExportApi';

export default function CouponsExport() {
  const { t } = useTranslation();
  const api = useExportApi();
  const { projectId } = useParams();

  const [downloading, setDownloading] = useState(false);
  const [downloadedCouponsCount, setDownloadedCouponsCount] = useState<
    number | undefined
  >();

  const [uploading, setUploading] = useState(false);
  const [uploadingDialog, setUploadingDialog] = useState(false);
  const [uploadedCouponsCount, setUploadedCouponsCount] = useState<
    number | undefined
  >();

  const handleCouponsDownload = useCallback(async () => {
    setDownloading(true);
    const data = await api.getCoupons(null);
    if (!data) {
      setDownloadedCouponsCount(0);
      setDownloading(false);
      return;
    }
    const jsonData = JSON.parse(data);
    setDownloadedCouponsCount(jsonData.length);
    downloadFile(data, `${projectId}-coupons-export.json`, 'application/json');
    setDownloading(false);
  }, [api, projectId]);

  const handleCouponsUpload = async (file: File) => {
    setUploadingDialog(false);
    setUploading(true);
    const data = await readFile(file);
    if (!data) {
      setUploading(false);
      setUploadedCouponsCount(0);
    }
    const jsonData = JSON.parse(data);
    await api.uploadCoupons(data);
    setUploadedCouponsCount(jsonData.length);
    setUploading(false);
  };

  return (
    <>
      <UploadDialog
        open={uploadingDialog}
        confirmationTitle={t('export.coupons.uploadConfirmation.title')}
        confirmationBody={t('export.coupons.uploadConfirmation.body')}
        onUpload={handleCouponsUpload}
        onClose={() => {
          setUploadingDialog(false);
        }}
        metadataParser={async (file: File) => {
          const data = await readFile(file);
          const jsonData = JSON.parse(data);
          return { Coupons: String(jsonData.length) };
        }}
      />

      <Paper
        legend={
          <Stack direction="row" gap={1} alignItems="center">
            <ProductsIcon style={{ fill: 'white' }} />
            <Typography variant="body2">
              {t('export.coupons.panelHeadline')}
            </Typography>
          </Stack>
        }
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-evenly"
        >
          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={handleCouponsDownload}
              startIcon={<DownloadIcon />}
              variant="contained"
              loading={downloading}
              loadingPosition="start"
            >
              {t('export.coupons.downloadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {downloadedCouponsCount !== undefined && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.coupons.downloadedHint', {
                      count: downloadedCouponsCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>

          <Stack direction="column" alignItems="flex-start" gap={2}>
            <Button
              onClick={() => {
                setUploadingDialog(true);
              }}
              startIcon={<UploadIcon />}
              variant="contained"
              loading={uploading}
              loadingPosition="start"
            >
              {t('export.coupons.uploadButton')}
            </Button>
            <Box sx={{ minHeight: '24px' }}>
              {uploadedCouponsCount !== undefined && (
                <Stack direction="row" gap={1} alignItems="center">
                  <CheckIcon color="success" fontSize="small" />
                  <Typography variant="caption">
                    {t('export.coupons.uploadedHint', {
                      count: uploadedCouponsCount,
                    })}
                  </Typography>
                </Stack>
              )}
            </Box>
          </Stack>
        </Stack>
      </Paper>
    </>
  );
}
